import { z } from 'zod';

// Recurring task enums and types
export enum RecurringFrequencyEnum {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  CUSTOM = 'CUSTOM',
}

export enum RecurringEndTypeEnum {
  NEVER = 'NEVER',
  ON_DATE = 'ON_DATE',
  AFTER_OCCURRENCES = 'AFTER_OCCURRENCES',
}

export enum RecurringMissedOccurrencePolicyEnum {
  NEXT_FUTURE = 'NEXT_FUTURE',
  BACKFILL = 'BACKFILL',
}

// Recurring pattern schema
export const RecurringPatternSchema = z.object({
  frequency: z.nativeEnum(RecurringFrequencyEnum),
  interval: z.number().min(1).default(1), // Every X days/weeks/months/years
  endType: z.nativeEnum(RecurringEndTypeEnum).default(RecurringEndTypeEnum.NEVER),
  missedOccurrencePolicy: z
    .nativeEnum(RecurringMissedOccurrencePolicyEnum)
    .default(RecurringMissedOccurrencePolicyEnum.NEXT_FUTURE),
  // Scheduled-date offset: number = spawned instances get scheduledFor set to
  // N days before the occurrence due date (0 = same day). null = explicitly no
  // auto-scheduled date on spawned instances. Absent ⇒ legacy behavior: infer
  // the offset from the completed instance's scheduledFor − dueDate. Ignored
  // while the series task has no due date (the occurrence date itself becomes
  // the scheduled date).
  scheduledOffsetDays: z.number().int().min(0).max(365).nullable().optional(),
  endDate: z.preprocess(
    value => {
      if (value == null || value instanceof Date) {
        return value
      }

      return new Date(String(value))
    },
    z.date().nullable().optional(),
  ), // Used when endType is ON_DATE
  maxOccurrences: z.number().min(1).nullable().optional(), // Used when endType is AFTER_OCCURRENCES
  // For weekly: which days of the week (0 = Sunday, 1 = Monday, etc.)
  weekDays: z.array(z.number().min(0).max(6)).optional(),
  // For monthly: day of month (1-31) or relative (first Monday, last Friday, etc.)
  monthDay: z.number().min(1).max(31).nullable().optional(),
  monthDayType: z.enum(['SPECIFIC', 'RELATIVE']).optional(), // SPECIFIC = day 15, RELATIVE = 2nd Tuesday
  monthRelativeWeek: z.number().min(1).max(5).nullable().optional(), // 1st, 2nd, 3rd, 4th, 5th (or last)
  monthRelativeDayOfWeek: z.number().min(0).max(6).nullable().optional(), // 0 = Sunday, 6 = Saturday
  // For annual: month and day
  yearMonth: z.number().min(1).max(12).nullable().optional(),
  yearDay: z.number().min(1).max(31).nullable().optional(),
  // Custom pattern for advanced users (cron-like expression)
  customPattern: z.string().nullable().optional(),
  // IANA timezone of the series (e.g. "America/Denver"). Captured when the
  // pattern is created/edited so background paths (cron sweep, bulk complete)
  // can resolve calendar days in the user's terms, not the server's UTC day.
  timeZone: z.string().nullable().optional(),
  // Chore rotation (personal spaces): when present, each spawned instance is
  // assigned to the next member in the list (round robin by instance number)
  // instead of copying the completed task's assignees. Absent ⇒ legacy
  // copy-assignees behavior, byte-identical for existing work recurrences.
  assigneeRotation: z
    .object({
      memberIds: z.array(z.string().uuid()).min(1),
      mode: z.literal('round_robin').default('round_robin'),
    })
    .nullable()
    .optional(),
});

export type RecurringPattern = z.infer<typeof RecurringPatternSchema>; 

export function parseRecurringPattern(pattern: unknown): RecurringPattern {
  return RecurringPatternSchema.parse(pattern)
}

export function normalizeRecurringPattern(
  pattern: unknown,
): RecurringPattern | null {
  if (pattern == null) {
    return null
  }

  const parsed = RecurringPatternSchema.safeParse(pattern)
  if (!parsed.success) {
    return null
  }

  return parsed.data
}
